import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import Vue from 'vue';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import { ConfigType } from '@core/plugins/config/config';
import _ from '@glittr/frontend-core/src/utils';
import Int64LookupModel from '@glittr/frontend-core/src/services/v2/model/int64-lookup-model';
import { IJSONSchema } from '@glittr/frontend-core/src/plugins/validation/IJSONSchema';
import personalDetailsVueSchema from './registration.vue.schema';
import PostOrganisationRequestModel from '../../../../../services/v2/model/post-organisation-request-model';
import OrganisationRequestModel from '../../../../../services/v2/model/organisation-request-model';
import ContactRequestModel from '../../../../../services/v2/model/contact-request-model';
import UserRequestModel from '../../../../../services/v2/model/user-request-model';
import UploadTdgDocumentRequestModel from '../../../../../services/v2/model/upload-tdg-document-request-model';
import GetTdgDocumentByExternalIdRequestModel from '../../../../../services/v2/model/get-tdg-document-by-external-id-request-model';

export default class OrganisationRegistrationPage extends PageBase {
  constructor() {
    super();
    this.combinedModels = new PostOrganisationRequestModel();
    this.combinedModels.organisation = new OrganisationRequestModel();
    this.combinedModels.organisation.contact = new ContactRequestModel();
    this.combinedModels.user = new UserRequestModel();
    this.combinedModelsForExistingOrganisation = new PostOrganisationRequestModel();
    this.combinedModelsForExistingOrganisation.organisation = new OrganisationRequestModel();
    this.combinedModelsForExistingOrganisation.organisation.contact = new ContactRequestModel();
    this.combinedModelsForExistingOrganisation.user = new UserRequestModel();
  }

  public set convertedError(x:any) { /* NOP */ }

  public get convertedError(): any {
    if (_.isObject(this.error) && _.isObject(this.error.data)) {
      const info = this.error.data.Info || {};
      const errorInfo = info.ErrorInfo || {};
      const details = errorInfo.Details || {};

      return {
        ...this.error,
        data: {
          ...this.error.data,
          result: this.error.data.Result,
          info: {
            ...info,
            statusCode: info.StatusCode,
            statusMessage: info.StatusMessage,
            pagingInfo: info.PagingInfo,
            isError: info.IsError,
            errorInfo: {
              ...errorInfo,
              details: { ...details },
            },
          },
        },
      };
    }
    return this.error;
  }

  public error?: any = undefined;

  public emailError: string | null = null;

  public logoDocuments: undefined | File[];

  public isLoading = false;

  public combinedModels: PostOrganisationRequestModel;

  public combinedModelsForExistingOrganisation: PostOrganisationRequestModel | undefined;

  public registrationSuccess = false;

  public supporterAmount: number | undefined;

  public eventItems: Int64LookupModel[] | undefined;

  public organisationExternalId: string | undefined = undefined;

  public get newOrganisation(): boolean {
    return !this.organisationExternalId;
  }

  public alreadyRegistratedOrganisation = false;

  public eventIdentifier: string | undefined = undefined;

  public eventName: string | undefined = undefined;

  public userName: string | undefined = undefined;

  public wantsNewUser = false;

  public genderOptions() {
    return [
      { id: 0, caption: Vue.$t('core.address.salutationEnum3') },
      { id: 1, caption: Vue.$t('core.address.salutationEnum1') },
      { id: 2, caption: Vue.$t('core.address.salutationEnum2') },
    ];
  }

  async initialize(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const organisationIdParam = urlParams.get('organisationid');
    this.organisationExternalId = organisationIdParam ?? undefined;

    this.eventIdentifier = this.getEventIdentifier('current-eventidentifier') as unknown as string;

    if (this.organisationExternalId != null) {
      this.organisationByOrganisationIdAndExternalIdDataSource.filter.externalId = this.organisationExternalId;
      this.organisationByOrganisationIdAndExternalIdDataSource.filter.eventIdentifier = this.eventIdentifier;
      const organisation = await this.organisationByOrganisationIdAndExternalIdDataSource.select();

      this.existingOrganisationDataSource.filter.externalId = this.organisationExternalId;
      const result = await this.existingOrganisationDataSource.select();
      if (result.data?.userName) {
        this.userName = result.data.userName;
      }

      if (organisation.data == null) {
        if (result.data.logoDocumentExternalId) {
          const requestDocument = new GetTdgDocumentByExternalIdRequestModel();
          requestDocument.externalId = result.data.logoDocumentExternalId;
          const document = await Vue.$service.v2.api.tdg_Documents.getTdgDocumentByExternalId(requestDocument);
          try {
            const fileContent = await Vue.$service.v2.api.tdg_Documents.downloadTdgDocument(requestDocument);
            const file = new File([fileContent.data], document.data.originalFileName || 'unknown Filename', { type: document.data.contentType });
            Vue.set(this, 'logoDocuments', [file]);
          } catch (error) {
            Vue.set(this, 'logoDocuments', []);
          }
        }

        if (result) {
          this.combinedModelsForExistingOrganisation!.organisation = new OrganisationRequestModel({
            name: result.data.organisationName,
            logoDocumentExternalId: result.data.logoDocumentExternalId,
            membersCount: result.data.membersCount,
            street: result.data.organisationStreet,
            houseNumber: result.data.organisationHouseNumber,
            zip: result.data.organisationZIP,
            city: result.data.organisationCity,
            isSupporter: result.data.isSupporter,
            acceptConditions: false, // User always needs to accept the conditions
          });
          this.combinedModelsForExistingOrganisation!.organisation.contact = new ContactRequestModel({
            salutation: result.data.organisationContactSalutation,
            firstName: result.data.organisationContactFirstName,
            lastName: result.data.organisationContactLastName,
            phoneNumber: result.data.organisationContactPhoneNumber,
            email: result.data.organisationContactEmail,
          });
          this.combinedModels!.organisation = this.combinedModelsForExistingOrganisation!.organisation;
          this.combinedModels!.organisation.contact = this.combinedModelsForExistingOrganisation!.organisation.contact;
        }
      } else {
        this.alreadyRegistratedOrganisation = true;
        this.eventsDataSource.filter.eventIdentifier = this.eventIdentifier;
        const eventRecord = await this.eventsDataSource.select();

        this.userDataSource.filter.id = result.data.organisationUserId;

        if (eventRecord?.data?.id !== undefined) {
          this.eventName = eventRecord.data.name;
        }
      }
    }
  }

  public organisationsDataSource = new DataSource({
    insertCommand: Vue.$service.v2.api.tdg_Organisations.postOrganisation,
  });

  public existingOrganisationDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Organisations.getOrganisationByExternalIdAsync,
  });

  public organisationByOrganisationIdAndExternalIdDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Organisations.getOrganisationByExternalIdAndEventIdentifierAsync,
  });

  public eventsDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Events.getEventByEventIdentifier,
  });

  public userDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.users.getUserById,
  });

  public saveNewOrganisations = async () => {
    if (this.newOrganisation) {
      if (this.combinedModels?.user?.email == null) {
        this.combinedModels!.user!.email = this.combinedModels!.organisation?.contact?.email;
      }
    }
    const schema = this.getValidationSchema();
    const valid = this.combinedModels!.validateWithSchema(schema);
    if (valid) {
      if (this.combinedModels?.user?.repeatPassword !== this.combinedModels?.user?.password) {
        this.error = Vue.$t('page.widget.error.passwordsDoNotMatch');
        return;
      }
      this.isLoading = true;
      try {
        const logoFiles = this.logoDocuments ?? [];
        if (logoFiles.length > 0) {
          const request = new UploadTdgDocumentRequestModel();
          request.uploadFile = this.logoDocuments?.[0];
          const documentId = await Vue.$service.v2.api.tdg_Documents.uploadTdgDocument(request);
          this.combinedModels!.organisation!.logoDocumentExternalId = documentId.data;
        } else {
          this.combinedModels!.organisation!.logoDocumentExternalId = undefined;
        }
        this.combinedModels!.organisation!.isActive = true;
        if (this.combinedModels) {
          this.combinedModels.eventIdentifier = this.eventIdentifier;
          if (this.newOrganisation === false) {
            this.combinedModels.existingOrganisationId = this.organisationExternalId;
            if (this.wantsNewUser === false) {
              this.combinedModels.user = undefined;
            }
          }
        }
        await this.organisationsDataSource.insert(this.combinedModels!);
        this.registrationSuccess = true;
        this.showExternalElement('widget-successelementid');
        this.isLoading = false;
      } catch (error: any) {
        Vue.$log.error(error);
        this.error = error;
        this.isLoading = false;
      }
    }
  };

  private getValidationSchema(): IJSONSchema {
    const baseSchema = JSON.parse(JSON.stringify(personalDetailsVueSchema));

    if (this.combinedModelsForExistingOrganisation?.organisation?.name) {
      if (baseSchema.properties) {
        if (!this.wantsNewUser) {
          delete baseSchema.properties.user;
          baseSchema.required = baseSchema.required?.filter((r: string) => r !== 'user');
        }
      }
    }

    return baseSchema;
  }

  public getExternalElement(configKey: keyof ConfigType) {
    const externalElementId = Vue.$config.getQueryOrValue(configKey);
    if (!_.isEmpty(externalElementId)) {
      const externalElement = document.getElementById(externalElementId!);
      if (externalElement === null) {
        Vue.$log.warning(`Element with id '${externalElementId}' not found.`);
      } else {
        return externalElement;
      }
    }
    return undefined;
  }

  public showExternalElement(configKey: keyof ConfigType) {
    const elem = this.getExternalElement(configKey);
    elem?.classList.add('glitWidgetElementVisible');
  }

  public hideExternalElement(configKey: keyof ConfigType) {
    const elem = this.getExternalElement(configKey);
    elem?.classList.remove('glitWidgetElementVisible');
  }

  public getEventIdentifier(configKey: keyof ConfigType) {
    const externalElementId = Vue.$config.getQueryOrValue(configKey);
    return externalElementId;
  }
}
